import { action, makeAutoObservable, observable, reaction, toJS } from "mobx";
import { findParent } from "utils/dom";
import { makeContext } from "utils/provider";
import { getMove } from "utils/touches";
import Block from "./block";
import SchemeStoreCreate from "./create";
import { JsonSchema } from "../../../store/json-schema";
import MainStore from "store";
import Scheme from "store/schema";

type Chain = string | [ string, number ] | { x: number, y: number }

const comp = (a: Chain, b: Chain) => {
  if (a === b) return true
  if (Array.isArray(a) && Array.isArray(b) && a[0] === b[0] && a[1] === b[1]) return true
  return false
}

class SchemeStore {

  name: string = ""
  slug: string | null = null
  cameraPos = { top: 0, left: 0 }
  blocks = observable.map<string, Block>([])
  selectedBlock: string | null = null
  schemeId: string | null = null
  isSaved: boolean | null = null

  createChain = false
  chains = observable.array<{ from: Chain, to: Chain }>([])
  create = new SchemeStoreCreate(this)
  editPropsMode = false

  constructor() {
    makeAutoObservable(this, {
      onKeyDown: action.bound
    })
  }

  init() {
    document.addEventListener("keydown", this.onKeyDown)
    const disposers = [
      reaction(() => toJS(this.blocks), action(() => {
        if (this.isSaved === null) return
        this.isSaved = false
      })),
      reaction(() => this.name, action(() => {
        this.isSaved = false
      })),
      reaction(() => this.slug, action(() => {
        this.isSaved = false
      }))
    ]

    return () => {
      document.removeEventListener("keydown", this.onKeyDown)
      disposers.forEach(d => d())
    }
  }

  loadScheme(scheme: JsonSchema | null) {
    setTimeout(action(() => {
      this.isSaved = true
    }), 100)
    if (!scheme) return
    this.name = scheme.name
    this.slug = scheme.slug !== this.nameAsSlug? scheme.slug: null

    for (let [ id, block ] of Object.entries(scheme.blocks)) {
      const _block = new Block(id)
      _block.type = block.type as typeof _block.type
      _block.position = block.position
      _block.emitters = block.emitters
      _block.data = block.data
      this.blocks.set(id, _block)
    }
    for (let chain of scheme.chains) {
      this.chains.push({ from: chain.from, to: chain.to })
    }
    this.isSaved = null
  }

  saveToJson() {
    const schema: JsonSchema = {
      name: this.name,
      slug: this.slug || this.nameAsSlug,
      blocks: {},
      chains: []
    }

    for (let block of this.blocks.values()) {
      schema.blocks[block.id] = {
        type: block.type,
        emitters: toJS(block.emitters),
        position: toJS(block.position),
        data: toJS(block.data)
      }
    }
    for (let chain of this.chains) {
      schema.chains.push({ from: toJS(chain.from) as [ string, number ], to: chain.to as string})
    }
    this.isSaved = true

    return schema
  }
  
  save(mainStore: MainStore) {
    if (!this.schemeId) return
    let schemaObj = mainStore.schemes.get(this.schemeId)
    if (!schemaObj) {
      schemaObj = new Scheme(this.schemeId)
      mainStore.addScheme(schemaObj)
    }
    schemaObj.setData(Object.assign({}, schemaObj.data, this.saveToJson()))
    mainStore.saveSchema(schemaObj)
    
    console.log(schemaObj.data)
  }

  onBlockClick(e: React.MouseEvent, blockId: string) {
    if (e.button !== 0) return
    if (this.selectElementCallback) {
      this.selectElementCallback(this.blocks.get(blockId) || null)
      this.selectElementCallback = null
      return
    }
    this.selectBlock(blockId)

    if ([ "button", "input", "textarea", "checkbox" ].includes((e.target as HTMLElement).tagName.toLowerCase())) return
    e.preventDefault()
    const block = this.blocks.get(blockId)!
    getMove(e, {
      onMove: action(({ deltaPos }) => {
        block.position.left += deltaPos.x
        block.position.top += deltaPos.y
      })
    })
  }

  onBlockDoubleClick(e: React.MouseEvent, blockId: string) {
    this.selectBlock(blockId)
    if ([ "button", "input", "textarea", "checkbox" ].includes((e.target as HTMLElement).tagName.toLowerCase())) return
    e.preventDefault()
    this.editPropsMode = true
  }

  get nameAsSlug() {
    return this.name.replaceAll(' ', '-').replace(/[,.!?]/g, '').toLowerCase()
  }

  selectBlock(blockId: string | null) {
    if (this.selectedBlock === blockId) return
    this.selectedBlock = blockId
    this.editPropsMode = false
  }

  onChainPointClick(e: React.MouseEvent, blockId: string, index?: number) {
    if (e.button !== 0) return
    e.stopPropagation()
    if (this.selectElementCallback) {
      this.selectElementCallback(this.blocks.get(blockId) || null)
      this.selectElementCallback = null
      return
    }
    this.selectBlock(blockId)
    this.createChain = true
    const startPos = observable({ x: e.clientX, y: e.clientY })

    let chain: { from: Chain, to: Chain }
    let mode: "to" | "from" = "to"
    if (typeof index === "number") {
      chain = observable({ from: [ blockId, index ], to: startPos })
      this.chains.push(chain)
    } else {
      const existsChain = this.chains.reduce((last, chain) => chain.to === blockId? chain: last)
      if (existsChain) {
        chain = existsChain
        chain.to = startPos
      } else {
        chain = observable({ from: startPos, to: blockId })
        mode = "from"
        this.chains.push(chain)
      }
    }
    
    getMove(e, {
      onMove: action(({ deltaPos }) => {
        startPos.x += deltaPos.x
        startPos.y += deltaPos.y
      }),
      onEnd: action(({ e }) => {
        this.createChain = false
        const block = findParent(e.target as HTMLElement, el => el.hasAttribute("data-block-id"))
        let insert = false
        if (block && mode === "to") {
          chain.to = block.getAttribute("data-block-id")!
          insert = true
        } 
        const chainPoint = findParent(e.target as HTMLElement, el => el.hasAttribute("data-chain-index"))
        if (block && chainPoint && mode === 'from') {
          chain.from = [ block.getAttribute("data-block-id")!, parseInt(chainPoint.getAttribute("data-chain-index")!) ]
          insert = true
        }
        const recursive = (chain.from as any)[0] === chain.to
        if (recursive || !insert || this.chains.find(ch => ch !== chain && comp(ch.from, chain.from) && comp(ch.to, chain.to))) {
          this.chains.remove(chain)
        }
        this.isSaved = false
      })
    })
  }

  getChainCoord(chain: Chain) {
    if (typeof chain === "string") {
      const block = this.blocks.get(chain)!
      return { 
        x: block.position.left + (block.collectorPos.left || 0) - this.cameraPos.left, 
        y: block.position.top + (block.collectorPos.top || 0) - this.cameraPos.top
      }
    }

    if (Array.isArray(chain)) {
      const block = this.blocks.get(chain[0])!
      return { 
        x: block.position.left + (block.emittersPos.get(chain[1])?.left || 0) - this.cameraPos.left, 
        y: block.position.top + (block.emittersPos.get(chain[1])?.top || 0) - this.cameraPos.top
      } 
    }

    return { x: chain.x, y: chain.y }
  }

  
  get chainPaths() {
    return this.chains.map(item => {
      const from = this.getChainCoord(item.from)
      const to = this.getChainCoord(item.to)
      const delta = Math.min(100, Math.abs(from.x - to.x) / 2)
      return [
        "M"+from.x, from.y,
        "C"+(from.x + delta), from.y+",",
        to.x - delta, to.y+",",
        to.x, to.y,
      ].join(" ")
    })
  }

  onBlockSystemClick(e: React.MouseEvent) {
    if (e.button === 1) {
      e.preventDefault()
      getMove(e, {
        onMove: action(({ deltaPos }) => {
          this.cameraPos.top -= deltaPos.y
          this.cameraPos.left -= deltaPos.x
        })
      })
      return
    }
    if (e.target !== e.currentTarget) return
    if (this.selectElementCallback) {
      this.selectElementCallback(null)
      this.selectElementCallback = null
      return
    }
    this.selectBlock(null)
  }

  onWheel(e: React.WheelEvent) {
    if (e.shiftKey) {
      this.cameraPos.left += e.deltaY
    } else {
      this.cameraPos.left += e.deltaX
      this.cameraPos.top += e.deltaY
    }
  }

  deleteBlock(blockId: string) {
    this.blocks.delete(blockId)
    if (this.selectedBlock === blockId) {
      this.selectBlock(null)
    }
    for (let i = 0; i < this.chains.length; i++) {
      const chain = this.chains[i]
      if (chain.to === blockId || (Array.isArray(chain.from) && chain.from[0] === blockId)) {
        this.chains.remove(chain)
        i--
      }
    }
  }
  
  selectElementCallback: ((state: Block | null) => void) | null = null
  selectElement(e: React.MouseEvent) {
    return new Promise<Block | null>((res) => {
      this.selectElementCallback = res
    })
  }

  get schemeSlug() {
    return this.slug !== null? this.slug: this.nameAsSlug
  }

  showBlockKeys = (window.localStorage.getItem("showBlockKeys") === "true")
  setShowBlockKeys(value: boolean) {
    this.showBlockKeys = value
    window.localStorage.setItem("showBlockKeys", value.toString())
  }
  
  provideKey(block: Block) {
    if (block.type === "array") return true
    if (block.type === "selectable" && block.data.useAsKey === "true") return true
    if (block.type === "group" && block.data.key) return true
    return false
  }
  
  getBlockKey(blockId: string, arr: string[] = []) {
    const firstElement = arr.length === 0
    const block = this.blocks.get(blockId)
    if (!block) return arr
    if (!firstElement && block.type === "array") {
      arr.unshift("<num>")
    }

    if (this.provideKey(block) || firstElement) {
      if (!firstElement && block.type === "array" && block.data.key?.endsWith('s') && block.data.keepS !== "true") {
        arr.unshift(block.data.key.slice(0, -1))
      } else {
        arr.unshift(block.data.key)
      }
    }
    
    const chain = this.chains.find(chain => chain.to === blockId)
    if (chain) {
      this.getBlockKey((chain.from as string[])[0] , arr)
    }

    return arr
  }

  onKeyDown(e: KeyboardEvent) {
    if (["INPUT", "TEXTAREA"].includes((e.target as HTMLElement).tagName)) return
    
    if (this.selectedBlock) {
      if (e.code === "Delete" || e.code === "Backspace") {
        this.deleteBlock(this.selectedBlock)
      }
      if (e.code === "F2") {
        const input = document.querySelector(`.block[data-block-id="${this.selectedBlock}"] .editable`) as HTMLDivElement
        input.dispatchEvent(new MouseEvent("dblclick"))
      }
    }
    if (e.code === "KeyI") {
      this.setShowBlockKeys(!this.showBlockKeys)
    }
  }

}

const [ SchemeEditorStoreProvider, useSchemeEditorStore ] = makeContext(() => new SchemeStore())
export { SchemeEditorStoreProvider, useSchemeEditorStore, SchemeStore }